<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay">
    <v-card>
      <v-card-title>Create Park</v-card-title>
      <v-form @submit.prevent="submit" @reset="reset">
        <v-card-text>
          <v-text-field
            v-model="form.parkname.value.value"
            :error-messages="form.parkname.errorMessage.value"
            label="Park Name"
          />
          <v-autocomplete
            label="Agency"
            v-model="form.agency.value.value"
            :error-messages="form.agency.errorMessage.value"
            :items="agencys"
            item-title="visual_name"
            item-value="id"
          />
          <v-text-field
            v-model="form.n_cp.value.value"
            :error-messages="form.n_cp.errorMessage.value"
            label="Number of CP [int number, ex: 3]"
            type="number"
          />
          <v-text-field
            v-model="form.price_cents.value.value"
            :error-messages="form.price_cents.errorMessage.value"
            label="Price [eurocents, int number, ex: 30]"
            type="number"
          />
          <v-text-field
            v-model="form.qr_codes.value.value"
            :error-messages="form.qr_codes.errorMessage.value"
            label="QR code [comma separated, ex: 1,2,3]"
          />
          <v-text-field
            v-model="form.email.value.value"
            :error-messages="form.email.errorMessage.value"
            label="Email"
          />
          <v-checkbox v-model="form.enable_email_report.value.value" label="Enable Email Report"/>
          <v-checkbox v-model="form.activate_gireve.value.value" label="Activate Gireve"/>
          <v-checkbox v-model="form.installed.value.value" label="Installed"/>
          <v-divider/>
          <v-card-title>Location</v-card-title>
          <v-text-field
            v-model="form.address.value.value"
            :error-messages="form.address.errorMessage.value"
            label="Insert your address [Rue Saint-Pierre 15]"
          />
          <v-text-field
            v-model="form.city.value.value"
            :error-messages="form.city.errorMessage.value"
            label="Insert your city [Paris]"
          />
          <v-text-field
            v-model="form.postal_code.value.value"
            :error-messages="form.postal_code.errorMessage.value"
            label="Postal Code [13035]"
          />
          <v-text-field
            v-model="form.country_a2.value.value"
            :error-messages="form.country_a2.errorMessage.value"
            label="Insert Country and click Search!"
          />
          <v-container>
            <l-map :zoom="zoom" :center="[mapLat, mapLon]"
                   style="width: 80%; height: 300px; margin-left: auto; margin-right: auto;">
              <l-tile-layer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                layer-type="base"
                name="OpenStreetMap"
              />
              <l-marker :visible="showMarker" :lat-lng="[form.latitude.value.value, form.longitude.value.value]"
                        draggable @update:lat-lng="moveMarker"/>
            </l-map>
          </v-container>
          <v-container class="d-flex justify-end">
            <v-btn @click="mySearchMapPosition">Search</v-btn>
          </v-container>
          <v-text-field
            v-model="form.latitude.value.value"
            :error-messages="form.latitude.errorMessage.value"
            type="number"
            label="Latitude"
          />
          <v-text-field
            v-model="form.longitude.value.value"
            :error-messages="form.longitude.errorMessage.value"
            type="number"
            label="Longitude"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn type="reset">Reset</v-btn>
          <v-btn type="submit">Submit</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </PanelPage>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import PanelPage from "../components/PanelPage.vue";
import { getFormFromSchema } from "../utils.js";
import { searchMapPosition, AddressNotFound } from "../remote/map.js";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";

// noinspection ES6UnusedImports
import L from "leaflet";
import { createPark } from "../remote/park.js";
import { fetchAgencys } from "../remote/agency.js";

// Use
const useAlerts = ref(null);

// Variables
const agencys = ref([]);

// State
const loadingOverlay = ref(false);
const zoom = ref(4);
const mapLat = ref(47.41322);
const mapLon = ref(8.219482);
const showMarker = ref(false);

// Validation Schema
const schema = yup.object({
  parkname: yup.string().required().matches(/^[\x20-\x7E]*$/, {
    message: 'Park name must contain only ASCII characters',
    excludeEmptyString: true,
  }),
  agency: yup.number().integer().required("A responsible agency is required"),
  n_cp: yup.number().required().positive().integer(),
  price_cents: yup.number().required(),
  qr_codes: yup.array().of(yup.number().integer().min(0)).transform((v) => v?.split(',').map((x) => Number(x.trim()))),
  email: yup.string().email(),
  enable_email_report: yup.bool(),
  activate_gireve: yup.bool(),
  installed: yup.bool(),
  address: yup.string().required(),
  city: yup.string().required(),
  postal_code: yup.string().required(),
  country_a2: yup.string().required().length(2),
  latitude: yup.number().required().min(-90).max(90),
  longitude: yup.number().required().min(-180).max(180),
  note: yup.string(),
});

const { handleSubmit, handleReset } = useForm({
  validationSchema: schema,
  initialValues: {
    price_cents: 30,
    latitude: 0,
    longitude: 0,
  }
});

const form = getFormFromSchema(schema);

// noinspection JSValidateTypes
const submit = handleSubmit(async (values) => {
  console.log(values);
  const qrcodes = values['qr_codes']?.split(',').map((x) => Number(x.trim()));
  let payload = {
    name: values['parkname'],
    agency: values['agency'],
    latitude: values['latitude'],
    longitude: values['longitude'],
    address: values['address'],
    postal_code: values['postal_code'],
    city: values['city'],
    country_alpha2: values['country_a2'].toUpperCase(),
    accept_gireve: values['activate_gireve'],
    chargingstations: [],
    installed: values['installed'],
    email: values['email'] || "",
    email_report: values['enable_email_report'],
    note: values['note'],
  };
  for (let i = 0; i < values['n_cp']; i++) {
    const cs = {
      price: values['price_cents'],
    };
    if (qrcodes !== undefined) {
      cs['qrcodeid'] = i < qrcodes.length ? qrcodes[i] : null;
    }
    payload.chargingstations.push(cs);
  }
  console.log(payload);
  loadingOverlay.value = true;
  // Create Park
  try {
    await createPark(useAlerts, payload);
  } catch (e) {
    useAlerts.value.errorAlert('Error during Park creation', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});

function reset() {
  showMarker.value = false;
  // noinspection JSValidateTypes
  handleReset();
}

async function mySearchMapPosition() {
  // Validation check
  const v = await Promise.all([form.address.validate(), form.city.validate(), form.country_a2.validate(), form.postal_code.validate()]);
  if (!v.every(p => p.valid === true)) {
    console.log("Abort search map position for failed validation");
    return;
  }
  try {
    const { lat, lon } = await searchMapPosition(
      form.address.value.value.toString(),
      form.city.value.value.toString(),
      form.country_a2.value.value.toString(),
      form.postal_code.value.value.toString()
    );
    // Set Latitude and Longitude to the Marker and other things
    form.latitude.setValue(lat);
    form.longitude.setValue(lon);
    mapLat.value = lat;
    mapLon.value = lon;
    zoom.value = 14;
    showMarker.value = true;
  } catch (e) {
    if (e instanceof AddressNotFound) {
      useAlerts.value.warningAlert("No address found", "Please check your informations and try again.");
    } else {
      useAlerts.value.errorAlert("Error trying to find the address", `${e.response?.status} ${e.response?.data}`);
    }
  }
}

function moveMarker(latlng) {
  // Trunc( N * 1e7) / 1e7 truncate at the 7th decimal place
  form.latitude.setValue(Math.trunc(latlng.lat * 1e7) / 1e7);
  form.longitude.setValue(Math.trunc(latlng.lng * 1e7) / 1e7);
}

onMounted(async () => {
  loadingOverlay.value = true;
  try {
    [
      agencys.value,
    ] = await Promise.all([
      fetchAgencys(),
    ]);
  } catch (e) {
    useAlerts.value.errorAlert('Impossible to load initial infos', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});

</script>

<style scoped>

</style>
